import ColumnTable, { type TableColumn } from "@/components/table/ColumnTable"
import { SearchShipmentsSortBy, ShipmentStatus, SystemRole, getShipmentStatusColor, type ShipmentLog } from "@/models"
import { useLoggedInUser } from "@/stores/loggedInUserStore"
import { formatEnum } from "@/utils/formatEnum"
import { css } from "vite-css-in-js"
import type { TdHTMLAttributes } from "vue"
import { defineComponent, type ReactiveComponent } from "vue-utils"
import { useShipmentsStore } from "../store"
import CancelShipmentColumn from "./CancelShipmentColumn"
import CompleteShipmentColumn from "./CompleteShipmentColumn"
import DeleteShipmentColumn from "./DeleteShipmentColumn"
import DocumentsColumn from "./DocumentsColumn"
import ShipmentDateColumn from "./ShipmentDateColumn"
import SortableShipmentHeading from "./SortableShipmentHeading"

const tableStyles = css`
	& > thead th {
		font-size: 0.95rem;
	}
	& > tbody > tr > td {
		background-color: var(--shipment-row-color) !important;
	}
	& > tbody > tr[data-status="Cancelled"] > td {
		text-decoration: line-through;
		color: #555555;
	}
`

function getStatus(shipment: ShipmentLog) {
	if (shipment.cancelled) return ShipmentStatus.Cancelled
	if (shipment.completed) return ShipmentStatus.Completed
	return ShipmentStatus.Active
}

const ShipmentsTable: ReactiveComponent = () => {
	const store = useShipmentsStore()
	const user = useLoggedInUser()

	const columns: Record<string, TableColumn<ShipmentLog>> = {
		shipmentNumber: {
			label: "Shipment No.",
			size: "auto",
			renderTh: () => <SortableShipmentHeading sort={SearchShipmentsSortBy.ShipmentNumber} text="Shipment No." />,
			renderTd: ({ item }) => <td style={{ textDecoration: "none" }}>{item.shipmentNumber}</td>,
		},
		status: {
			label: "Status",
			size: "minmax(max-content, 0.5fr)",
			renderTh: () => <SortableShipmentHeading sort={SearchShipmentsSortBy.Status} text="Status" />,
			renderTd: ({ item }) => <td style={{ textDecoration: "none" }}>{formatEnum(getStatus(item))}</td>,
		},
		dateCreated: {
			label: "Prenotification Date",
			size: "minmax(max-content, 0.75fr)",
			renderTh: () => <SortableShipmentHeading sort={SearchShipmentsSortBy.DateCreated} text="Prenotification Date" />,
			renderContent: ({ item }) => item.createdBy.timeStamp.toGBDateString(),
		},
		shipmentDate: {
			label: "Shipment Date",
			size: "minmax(max-content, 0.75fr)",
			renderTh: () => <SortableShipmentHeading sort={SearchShipmentsSortBy.ShipmentDate} text="Shipment Date" />,
			renderTd: ({ item }) => <ShipmentDateColumn shipment={item} />,
		},
		notificationOfShipments: {
			label: "Notification of Shipments",
			size: "minmax(min-content, 1fr)",
			renderTh: () => (
				<SortableShipmentHeading
					sort={SearchShipmentsSortBy.NotificationsOfShipment}
					text="Notification of Shipments"
				/>
			),
			renderTd: ({ item }) => <DocumentsColumn shipment={item} type="notificationOfShipments" />,
		},
		recoveryAtFacility: {
			label: "Recovery At Facility",
			size: "minmax(min-content, 1fr)",
			renderTh: () => (
				<SortableShipmentHeading sort={SearchShipmentsSortBy.RecoveryAtFacility} text="Recovery At Facility" />
			),
			renderTd: ({ item }) => <DocumentsColumn shipment={item} type="recoveryAtFacility" />,
		},
		additionalDocuments: {
			label: "Additional Documents",
			size: "minmax(min-content, 1fr)",
			renderTh: () => (
				<SortableShipmentHeading sort={SearchShipmentsSortBy.AdditionalDocuments} text="Additional Documents" />
			),
			renderTd: ({ item }) => <DocumentsColumn shipment={item} type="additionalDocuments" />,
		},
		cancelled: {
			label: "Cancelled?",
			size: "minmax(8rem, 0.35fr)",
			renderTh: () => <SortableShipmentHeading sort={SearchShipmentsSortBy.Cancelled} text="Cancelled?" />,
			renderTd: ({ item }) => <CancelShipmentColumn shipment={item} />,
		},
		completed: {
			label: "Completed?",
			size: "minmax(8rem, 0.35fr)",
			renderTh: () => <SortableShipmentHeading sort={SearchShipmentsSortBy.Completed} text="Completed?" />,
			renderTd: ({ item }) => <CompleteShipmentColumn shipment={item} />,
		},
	}

	if (user.atLeastHasRole(SystemRole.SuperAdmin)) {
		columns.delete = {
			label: "",
			size: "max-content",
			renderTh: () => <th style={{ backgroundColor: "transparent", border: "none" }} />,
			renderTd: ({ item }) => <DeleteShipmentColumn shipment={item} />,
		}
	}

	return () => (
		<ColumnTable
			getKey={(shipment) => shipment.id}
			entities={store.getResults().logs}
			rowAttributes={(shipment) => {
				const status = getStatus(shipment)
				return {
					"data-status": status,
					style: { "--shipment-row-color": getShipmentStatusColor(status) },
				} as TdHTMLAttributes
			}}
			class={tableStyles}
			columns={columns}
		/>
	)
}

export default defineComponent(ShipmentsTable)
